import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAuthContext } from '../../auth/data/context/AuthContext';
import { AdminUserClient } from '../data/client/AdminUserClient';
import { InterviewIQAdminUserProfile } from '../data/interface/adminUserListResponse';
import AddAdminUserModal, { UserSelectionData, AdminUserModalProps } from '../components/AddAdminUserModal';
import Alert, { AlertProps } from '../../../shared/components/Alert';
import { UserPlus, Search, ShieldCheck, MailWarning, Trash2, Edit2, ShieldAlert, Users, X } from 'lucide-react';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../../../shared/components/Table/Table';
import Loader from '../../../shared/components/Loader';
import '../../learnerControl/styles/LearnerStyles.css';

const UserManagementPage: React.FC = () => {
  const { state } = useAuthContext();

  const [globalAlert, setGlobalAlert] = useState<AlertProps>({
    show: false,
    title: '',
    message: '',
    onClose: () => setGlobalAlert(prev => ({ ...prev, show: false }))
  });

  const showAlert = (props: { type: 'success' | 'error' | 'warning' | 'info'; title: string; message: string }) => {
    setGlobalAlert({
      show: true,
      type: props.type,
      title: props.title,
      message: props.message,
      onClose: () => setGlobalAlert(prev => ({ ...prev, show: false }))
    });
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
    setGlobalAlert({
      show: true,
      type: 'warning',
      title,
      message,
      primaryButtonText: 'Yes',
      secondaryButtonText: 'No',
      onPrimaryClick: () => {
        setGlobalAlert(prev => ({ ...prev, show: false }));
        onConfirm();
      },
      onSecondaryClick: () => setGlobalAlert(prev => ({ ...prev, show: false })),
      onClose: () => setGlobalAlert(prev => ({ ...prev, show: false }))
    });
  };

  const [users, setUsers] = useState<InterviewIQAdminUserProfile[]>([]);
  const [you, setYou] = useState<InterviewIQAdminUserProfile | null>(null);
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [pendingUsersCount, setPendingUsersCount] = useState(0);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading users...');
  const limit = 25;

  const [modalState, setModalState] = useState<{
    show: boolean;
    identity: AdminUserModalProps["identity"];
    title: string;
    message?: string;
    userName?: string;
    emailAddress?: string;
    action?: (data: UserSelectionData) => void;
  }>({
    show: false,
    identity: "add_user",
    title: ""
  });

  const isSuperAdmin = state.user?.role_name === "Super Admin";

  const fetchUsers = async () => {
    setLoadingMessage('Loading users...');
    setLoading(true);
    try {
      const response = await AdminUserClient.getAdminUsers(page, limit, searchQuery, searchQuery);
      setYou(response.you);
      setUsers(response.others || []);
      setActiveUsersCount(response.active_users);
      setPendingUsersCount(response.pending_approval_users);
      setTotalUsersCount(response.total_users);
    } catch (err: any) {
      showAlert({
        type: 'error',
        title: 'Error',
        message: err?.response?.data?.error || 'Failed to fetch users'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery, isSuperAdmin]);

  const handleAddUser = async (data: UserSelectionData) => {
    setModalState(prev => ({ ...prev, show: false }));
    setLoadingMessage('Adding user...');
    setLoading(true);
    try {
      const res = await AdminUserClient.addAdminUser(data.name, data.emailId);
      showAlert({ type: 'success', title: 'Success', message: res.detail });
      fetchUsers();
    } catch (err: any) {
      showAlert({ type: 'error', title: 'Error', message: err?.response?.data?.error || 'Failed to add user' });
      setLoading(false);
    }
  };

  const handleEditUser = async (data: UserSelectionData) => {
    setModalState(prev => ({ ...prev, show: false }));
    if (!modalState.emailAddress) return;
    setLoadingMessage('Updating user...');
    setLoading(true);
    try {
      const res = await AdminUserClient.editAdminUser(modalState.emailAddress, data.name);
      showAlert({ type: 'success', title: 'Success', message: res.detail });
      fetchUsers();
    } catch (err: any) {
      showAlert({ type: 'error', title: 'Error', message: err?.response?.data?.error || 'Failed to update user' });
      setLoading(false);
    }
  };

  const handleDeleteUser = async (email: string) => {
    setLoadingMessage('Deleting user...');
    setLoading(true);
    try {
      const res = await AdminUserClient.deleteAdminUser(email);
      showAlert({ type: 'success', title: 'Success', message: res.detail });
      fetchUsers();
    } catch (err: any) {
      showAlert({ type: 'error', title: 'Error', message: err?.response?.data?.error || 'Failed to delete user' });
      setLoading(false);
    }
  };

  const handleRevokeInvite = async (email: string) => {
    setLoadingMessage('Revoking invitation...');
    setLoading(true);
    try {
      const res = await AdminUserClient.revokeAdminUserInvite(email);
      showAlert({ type: 'success', title: 'Success', message: res.detail });
      fetchUsers();
    } catch (err: any) {
      showAlert({ type: 'error', title: 'Error', message: err?.response?.data?.error || 'Failed to revoke invite' });
      setLoading(false);
    }
  };

  const handleResendInvite = async (email: string) => {
    setLoadingMessage('Sending invitation...');
    setLoading(true);
    try {
      const res = await AdminUserClient.resendAdminUserInvite(email);
      showAlert({ type: 'success', title: 'Success', message: res.detail });
    } catch (err: any) {
      showAlert({ type: 'error', title: 'Error', message: err?.response?.data?.error || 'Failed to resend invite' });
    } finally {
      setLoading(false);
    }
  };

  const tableData = useMemo(() => {
    const data = [...users];
    if (you && page === 1) {
      data.unshift({ ...you, interviewiq_admin_id: 'you_id' });
    }
    return data;
  }, [users, you, page]);

  if (!isSuperAdmin) {
    return (
      <motion.div className="learner-page" style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center' }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
        <ShieldAlert size={64} color="#ef4444" style={{ marginBottom: '20px' }} />
        <h2 style={{ color: 'var(--text-heading)', marginBottom: '8px' }}>Access Denied</h2>
        <p style={{ color: 'var(--text-muted)' }}>You do not have permission to view this page.<br />Only Super Admins can access user management.</p>
      </motion.div>
    );
  }

  return (
    <motion.div className="learner-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.25 }}>
      <Alert {...globalAlert} />
      <Loader isLoading={loading} message={loadingMessage} />

      {/* Hero & Stats */}
      <div className="lp-hero" style={{ flexDirection: 'column', gap: '24px', flexShrink: 0 }}>
        {/* Row 1: Title */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
          <div className="lp-hero-left">
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#0f172a', letterSpacing: '-0.5px', fontWeight: 800 }}>User Management</h1>
            <span className="lp-hero-chip" style={{ color: '#059669', fontSize: '14px', background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '4px 14px', borderRadius: '20px', fontWeight: 600 }}>
              Manage admin users and their access permissions.
            </span>
          </div>
        </div>

        {/* Row 2: Stats & Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          {/* Stat Strip */}
          <div className="lp-stat-strip" style={{ flex: 1, maxWidth: '800px', margin: 0, gap: '16px' }}>
            <div className="lp-stat" style={{ padding: '12px 16px', gap: '12px' }}>
              <div className="lp-stat-icon green" style={{ width: 36, height: 36 }}>
                <ShieldCheck size={18} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.95rem', color: '#64748b', fontWeight: 600 }}>Active:</span>
                <strong style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{activeUsersCount}</strong>
              </div>
            </div>
            <div className="lp-stat" style={{ padding: '12px 16px', gap: '12px' }}>
              <div className="lp-stat-icon amber" style={{ width: 36, height: 36 }}>
                <MailWarning size={18} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.95rem', color: '#64748b', fontWeight: 600 }}>Pending:</span>
                <strong style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{pendingUsersCount}</strong>
              </div>
            </div>
            <div className="lp-stat" style={{ padding: '12px 16px', gap: '12px' }}>
              <div className="lp-stat-icon sky" style={{ width: 36, height: 36 }}>
                <Users size={18} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.95rem', color: '#64748b', fontWeight: 600 }}>Total:</span>
                <strong style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{totalUsersCount}</strong>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="btn-primary"
              onClick={() => setModalState({
                show: true,
                identity: "add_user",
                title: "Add New Admin User",
                action: handleAddUser
              })}
            >
              <UserPlus size={16} />
              Add User
            </button>
          </div>
        </div>
      </div>

      {/* Table Panel */}
      <div className="lp-table-panel">
        {/* Toolbar */}
        <div className="lp-table-panel-head" style={{ padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.3px' }}>All Users</h2>
          <div className="lp-search" style={{ height: 40, width: 280, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', position: 'relative' }}>
            <Search size={16} color="#94a3b8" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="lp-table-wrapper" style={{ overflowX: 'auto' }}>
          {tableData.length === 0 && !loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', gap: '16px' }}>
              <svg width="56" height="56" fill="none" stroke="#cbd5e1" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>No Users Found</h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#94a3b8' }}>
                  {searchQuery ? 'No users match your search criteria.' : 'No admin users have been added yet.'}
                </p>
              </div>
            </div>
          ) : (
            <Table className="learner-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email Address</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="actions-header">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tableData.map((row) => (
                  <TableRow 
                    key={row.email_address} 
                    style={
                      row.interviewiq_admin_id === 'you_id' ? { backgroundColor: '#f0fdfa' } :
                      row.status === 'Pending' ? { backgroundColor: '#fffbeb' } :
                      {}
                    }
                  >
                    <TableCell>
                      <div className="lp-user-cell" style={{ justifyContent: 'flex-start' }}>
                        <div 
                          className="lp-avatar"
                          style={row.status === 'Pending' ? { background: '#f59e0b' } : {}}
                        >
                          {row.full_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="lp-user-name">
                          {row.interviewiq_admin_id === 'you_id' ? `${row.full_name} (You)` : row.full_name}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="lp-user-email" style={{ color: '#64748b', fontSize: '0.85rem' }}>
                        {row.email_address}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`lp-badge ${row.role_name === 'Super Admin' ? 'active' : 'review'}`}>
                        <span className="lp-badge-dot" />
                        {row.role_name}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`lp-badge ${row.status === 'Active' ? 'active' : 'pending'}`}>
                        <span className="lp-badge-dot" />
                        {row.status}
                      </span>
                    </TableCell>
                    <TableCell className="actions-cell">
                      <div className="actions-row">
                        {row.interviewiq_admin_id !== 'you_id' && row.role_name !== 'Super Admin' && (
                          <>
                            {row.status === 'Pending' && (
                              <button
                                className="action-btn warning"
                                title="Resend Invitation"
                                onClick={() => handleResendInvite(row.email_address)}
                              >
                                <MailWarning size={16} />
                              </button>
                            )}
                            <button
                              className="action-btn edit"
                              title="Edit User"
                              onClick={() => setModalState({
                                show: true,
                                identity: 'edit_user',
                                title: 'Edit User',
                                message: row.role_name,
                                userName: row.full_name,
                                emailAddress: row.email_address,
                                action: handleEditUser
                              })}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              className="action-btn danger"
                              title={row.status === 'Pending' ? 'Revoke Invitation' : 'Delete User'}
                              onClick={() => {
                                if (row.status === 'Pending') {
                                  showConfirm('Revoke Invitation', `Are you sure you want to revoke the invitation for ${row.full_name}?`, () => handleRevokeInvite(row.email_address));
                                } else {
                                  showConfirm('Delete User', `Are you sure you want to delete ${row.full_name}?`, () => handleDeleteUser(row.email_address));
                                }
                              }}
                            >
                              {row.status === 'Pending' ? <X size={16} /> : <Trash2 size={16} />}
                            </button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Pagination */}
        {totalUsersCount > limit && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 24px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <div style={{ color: '#64748b', fontSize: '0.875rem' }}>
              Page {page}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="btn-ghost"
                style={{ height: '36px', padding: '0 16px', fontSize: '0.85rem' }}
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                className="btn-ghost"
                style={{ height: '36px', padding: '0 16px', fontSize: '0.85rem' }}
                disabled={tableData.length < limit}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <AddAdminUserModal
        show={modalState.show}
        identity={modalState.identity}
        title={modalState.title}
        message={modalState.message}
        userName={modalState.userName}
        emailAddress={modalState.emailAddress}
        onClose={() => setModalState(prev => ({ ...prev, show: false }))}
        onConfirm={modalState.action}
        buttonText={modalState.identity === 'view_user' ? 'Close' : 'Save'}
      />
    </motion.div>
  );
};

export default UserManagementPage;
